import { useEffect, useState } from 'react';
import CampoImporto from '../components/CampoImporto';
import { Link } from 'react-router-dom';
import { costiConsulentiApi, consulentiApi, commesseApi, offerteConsulentiApi } from '../api';
import Modal from '../components/Modal';
import SelettoreCommessa from '../components/SelettoreCommessa';
import ControlloConsulenti from '../components/ControlloConsulenti';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const empty = { consulente_id: '', commessa_id: '', offerta_consulente_id: '', descrizione: '', ore: '', importo: '', data_imputazione: new Date().toISOString().slice(0,10), nota_interna: '', tipo: 'fattura', numero_documento: '', data_pagamento: '' };

export default function CostiConsulenti() {
  const [rows, setRows] = useState([]);
  const [consulenti, setConsulenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [offerteConsulenti, setOfferteConsulenti] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [filtroConsulente, setFiltroConsulente] = useState('');
  const [vista, setVista] = useState('controllo');   // il controllo è la domanda più frequente

  const load = () => costiConsulentiApi.list({ consulente_id: filtroConsulente || undefined }).then(setRows);
  useEffect(() => { load(); }, [filtroConsulente]);
  useEffect(() => {
    consulentiApi.list().then(setConsulenti);
    commesseApi.list().then(setCommesse);
    offerteConsulentiApi.list().then(setOfferteConsulenti);
  }, []);

  const openNew = () => { setForm(empty); setModal({ mode: 'new' }); };
  const openEdit = (r) => {
    setForm({ ...r, data_imputazione: r.data_imputazione?.slice(0,10)||'', data_pagamento: r.data_pagamento?.slice(0,10)||'' });
    setModal({ mode: 'edit', id: r.id });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'new') await costiConsulentiApi.create(form);
      else await costiConsulentiApi.update(modal.id, form);
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm('Eliminare?')) return;
    await costiConsulentiApi.delete(id); load();
  };

  // Ricerca libera: si pensa per cliente, e il numero di commessa da solo non
  // basta a ritrovare un costo. Cerca anche commessa, descrizione e documento.
  const [cerca, setCerca] = useState('');
  const t = cerca.trim().toLowerCase();
  const visibili = !t ? rows : rows.filter(r =>
    [r.cliente_nome, r.commessa_numero, r.commessa_titolo, r.descrizione,
     r.numero_documento, r.offerta_consulente_numero]
      .some(v => String(v || '').toLowerCase().includes(t)));

  // I totali seguono il filtro: "quanto abbiamo speso su questo cliente" è
  // proprio la domanda per cui si cerca.
  const totale = visibili.reduce((s, r) => s + parseFloat(r.importo), 0);
  const totalOre = visibili.reduce((s, r) => s + parseFloat(r.ore), 0);
  const f = (k) => ({ value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });

  // Offerte consulenti filtrate per commessa selezionata nel form
  const ocFiltrate = offerteConsulenti.filter(oc => !form.commessa_id || String(oc.commessa_id) === String(form.commessa_id));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="page-title">Consulenti — costi e controllo</h1>
        <button className="btn-primary" onClick={openNew}>+ Registra costo</button>
      </div>

      <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit">
        {[['controllo', 'Controllo fatturazione'], ['righe', 'Costi registrati']].map(([v, l], i) => (
          <button key={v} onClick={() => setVista(v)}
            className={`px-3 py-1.5 text-sm ${i > 0 ? 'border-l border-gray-300' : ''} ${vista === v ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {l}
          </button>
        ))}
      </div>

      {vista === 'controllo' && <ControlloConsulenti />}

      {vista === 'righe' && (<>
      <div className="flex items-center gap-3 flex-wrap">
        <select className="input max-w-xs" value={filtroConsulente} onChange={e => setFiltroConsulente(e.target.value)}>
          <option value="">Tutti i consulenti</option>
          {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
        </select>
        {/* La larghezza sta sul contenitore: .input impone w-full */}
        <div className="w-72">
          <input className="input" type="search" placeholder="Cerca cliente, commessa, descrizione…"
                 value={cerca} onChange={e => setCerca(e.target.value)} />
        </div>
        <span className="text-sm text-gray-500 ml-auto">
          {visibili.length}{t ? ` di ${rows.length}` : ''} righe · <strong>{totalOre}h</strong> · <strong>{fmt(totale)}</strong>
        </span>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Data</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Consulente</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Commessa</th>
              <th className="px-4 py-3">Offerta rif.</th>
              <th className="px-4 py-3">Descrizione</th>
              <th className="px-4 py-3">Ore</th>
              <th className="px-4 py-3">Importo</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {visibili.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">
                {t ? `Nessun costo corrisponde a "${cerca}"` : 'Nessun costo registrato'}
              </td></tr>
            )}
            {visibili.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="px-4 py-3 text-gray-500">{fmtDate(r.data_imputazione)}</td>
                <td className="px-4 py-3">
                  {r.tipo === 'consuntivo'
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Consuntivo</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">Fattura</span>}
                  {r.numero_documento && <div className="text-xs font-mono text-gray-400 mt-0.5">{r.numero_documento}</div>}
                </td>
                <td className="px-4 py-3 font-medium">{r.consulente_nome} {r.consulente_cognome}</td>
                <td className="px-4 py-3 text-gray-700">{r.cliente_nome || '—'}</td>
                <td className="px-4 py-3">
                  <Link to={`/commesse/${r.commessa_id}`} className="text-brand-600 hover:underline">{r.commessa_numero}</Link>
                </td>
                <td className="px-4 py-3 text-gray-500 font-mono">{r.offerta_consulente_numero || '—'}</td>
                <td className="px-4 py-3">{r.descrizione}</td>
                <td className="px-4 py-3 text-gray-600">{r.ore}h</td>
                <td className="px-4 py-3 font-medium text-red-600">{fmt(r.importo)}</td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}>Modifica</button>
                  <button className="btn-danger btn-sm" onClick={() => del(r.id)}>Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>)}

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Imputa costo consulente' : 'Modifica costo'} onClose={() => setModal(null)} size="lg">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Consulente *</label>
              <select className="input" value={form.consulente_id || ''} onChange={e => setForm(p => ({ ...p, consulente_id: e.target.value }))}>
                <option value="">— Seleziona —</option>
                {consulenti.map(c => <option key={c.id} value={c.id}>{c.nome} {c.cognome}</option>)}
              </select>
            </div>
            <div>
              <label className="label">
                Commessa *
                <span className="ml-1 text-xs text-gray-400 font-normal">(cercabile per cliente)</span>
              </label>
              <SelettoreCommessa
                commesse={commesse}
                value={form.commessa_id}
                onChange={v => setForm(p => ({ ...p, commessa_id: v, offerta_consulente_id: '' }))}
                placeholder="— Seleziona la commessa —"
              />
            </div>
            <div className="col-span-2">
              <label className="label">Offerta consulente (rif. opzionale)</label>
              <select className="input" value={form.offerta_consulente_id || ''} onChange={e => setForm(p => ({ ...p, offerta_consulente_id: e.target.value }))}>
                <option value="">— Nessuna —</option>
                {ocFiltrate.map(oc => <option key={oc.id} value={oc.id}>{oc.consulente_nome} {oc.consulente_cognome} — {oc.numero || `#${oc.id}`} — {fmt(oc.importo_max)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Tipo di registrazione *</label>
              <select className="input" value={form.tipo || 'fattura'}
                      onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}>
                <option value="fattura">Fattura del consulente</option>
                <option value="consuntivo">Consuntivo (non ancora fatturato)</option>
              </select>
            </div>
            <div>
              <label className="label">N. documento</label>
              <input className="input" placeholder="es. 12/2026" {...f('numero_documento')} />
            </div>
            <div className="col-span-2"><label className="label">Descrizione *</label><input className="input" {...f('descrizione')} /></div>
            <div><label className="label">Ore</label><input className="input" type="number" step="0.5" {...f('ore')} /></div>
            <div><label className="label">Importo (€) *</label><CampoImporto className="input" value={form.importo} onChange={v => setForm(p => ({ ...p, importo: v }))} /></div>
            <div><label className="label">Data documento *</label><input className="input" type="date" {...f('data_imputazione')} /></div>
            {form.tipo !== 'consuntivo' && (
              <div>
                <label className="label">
                  Data pagamento
                  <span className="ml-1 text-xs text-gray-400 font-normal">(vuota = da pagare)</span>
                </label>
                <input className="input" type="date" {...f('data_pagamento')} />
              </div>
            )}
            <div className="col-span-2"><label className="label">Nota interna</label><textarea className="input" rows={2} {...f('nota_interna')} /></div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Salvo...' : 'Salva'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
